import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Radio, Button, Descriptions, Tag, Alert, Statistic, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { ShiftClosingDto, ReviewShiftClosingDto } from '../../types/cashRegister';
import { useCurrency } from '../../hooks/useCurrency';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import ShiftClosingAmounts, { getDifferenceColor, getDifferencePrefix } from './ShiftClosingAmounts';

const { TextArea } = Input;

interface ReviewShiftClosingModalProps {
  visible: boolean;
  shiftClosing: ShiftClosingDto | null;
  onCancel: () => void;
  onSubmit: (data: ReviewShiftClosingDto) => void;
  loading?: boolean;
}

const ReviewShiftClosingModal: React.FC<ReviewShiftClosingModalProps> = ({
  visible,
  shiftClosing,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const { format: formatCurrency } = useCurrency();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [action, setAction] = useState<'Approve' | 'Reject'>('Approve');

  useEffect(() => {
    if (visible && shiftClosing) {
      form.resetFields();
      setAction('Approve');
    }
  }, [visible, shiftClosing, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  if (!shiftClosing) return null;

  const renderDifference = (diff: number) => (
    <Statistic
      title="Diferencia"
      value={Math.abs(diff)}
      prefix={getDifferencePrefix(diff)}
      suffix={diff === 0 ? '✓' : ''}
      valueStyle={{
        color: getDifferenceColor(diff),
        fontSize: isMobile ? '14px' : '18px'
      }}
    />
  );

  return (
    <Modal
      title="Revisar Cierre de Turno"
      open={visible}
      onCancel={onCancel}
      width={isMobile ? '100%' : 900}
      style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
      bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', padding: '12px' } : {}}
      footer={
        shiftClosing.status !== 'Pending' ? (
          <Button onClick={onCancel} block={isMobile}>
            Cerrar
          </Button>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 8,
            width: '100%',
            justifyContent: 'flex-end',
          }}>
            <Button
              key="cancel"
              onClick={onCancel}
              disabled={loading}
              block={isMobile}
            >
              Cancelar
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              danger={action === 'Reject'}
              icon={action === 'Approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              block={isMobile}
            >
              {action === 'Approve' ? 'Aprobar' : 'Rechazar'}
            </Button>
          </div>
        )
      }
      destroyOnClose
    >
      <Alert
        message={isMobile ? "Revisa los montos" : "Revisa los montos contados vs los montos del sistema"}
        description={isMobile ? undefined : "Las diferencias positivas indican sobrante (sistema > contado), las negativas indican faltante (contado > sistema)"}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Descriptions bordered size="small" column={isMobile ? 1 : 2}>
        <Descriptions.Item label="Empleado">
          {shiftClosing.createdByUserName}
        </Descriptions.Item>
        <Descriptions.Item label="Fecha">
          {formatInTimeZone(shiftClosing.createdAt, 'DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label="Estado" span={isMobile ? 1 : 2}>
          <Tag color={shiftClosing.status === 'Pending' ? 'orange' : 'blue'}>
            {shiftClosing.status === 'Pending' ? 'Pendiente' : shiftClosing.status}
          </Tag>
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">{isMobile ? 'Montos' : 'Comparación de Montos'}</Divider>

      <ShiftClosingAmounts
        shiftClosing={shiftClosing}
        isMobile={isMobile}
        formatCurrency={formatCurrency}
        renderDifference={renderDifference}
      />

      {shiftClosing.employeeNotes && (
        <>
          <Divider orientation="left">Notas del Empleado</Divider>
          <Alert
            message="Comentarios del empleado sobre este cierre"
            description={shiftClosing.employeeNotes}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </>
      )}

      {shiftClosing.status === 'Pending' && (
        <>
          <Divider orientation="left">Decisión de Revisión</Divider>

          <Form form={form} layout="vertical">
            <Form.Item
              name="action"
              label="Acción"
              rules={[{ required: true, message: 'Selecciona una acción' }]}
              initialValue="Approve"
            >
              <Radio.Group onChange={(e) => setAction(e.target.value)} buttonStyle="solid">
                <Radio.Button value="Approve">
                  <CheckCircleOutlined /> Aprobar
                </Radio.Button>
                <Radio.Button value="Reject">
                  <CloseCircleOutlined /> Rechazar
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {action === 'Reject' && (
              <Form.Item
                name="rejectionReason"
                label="Motivo del Rechazo"
                rules={[
                  { required: true, message: 'Debes especificar el motivo del rechazo' },
                  { max: 500, message: 'Máximo 500 caracteres' }
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="Explica por qué rechazas este cierre de turno..."
                  maxLength={500}
                />
              </Form.Item>
            )}

            <Form.Item
              name="reviewNotes"
              label="Notas de Revisión (opcional)"
            >
              <TextArea
                rows={3}
                placeholder="Agrega cualquier observación sobre la revisión..."
                maxLength={1000}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default ReviewShiftClosingModal;
